import React from "react";
import { useField } from "formik";
import { Ruler } from "lucide-react";
import SectionCard from "../ui/SectionCard";
import Field from "../ui/Field";
import {
  BARCODE_HELPER_TEXT,
  BARCODE_MAX_LENGTH,
} from "../barcode";

const digitsOnly = (s) => String(s ?? "").replace(/\D/g, "");

const NumericCell = ({
  name,
  label,
  placeholder = "0",
  required,
  suffix,
  parseValue,
}) => {
  const [, meta, helpers] = useField(name);

  const handleChange = (e) => {
    const digits = digitsOnly(e.target.value);
    helpers.setValue(parseValue(digits));
  };

  return (
    <Field
      label={label}
      required={required}
      htmlFor={name}
      error={meta.touched ? meta.error : null}
    >
      <div className="pf-input-group">
        <input
          id={name}
          name={name}
          type="text"
          inputMode="numeric"
          value={meta.value ?? ""}
          onChange={handleChange}
          onBlur={() => helpers.setTouched(true)}
          placeholder={placeholder}
          className="pf-input"
          aria-invalid={meta.touched && !!meta.error}
        />
        {suffix ? (
          <span className="pf-input-group__suffix">{suffix}</span>
        ) : null}
      </div>
    </Field>
  );
};

const BarcodeCell = () => {
  const [, meta, helpers] = useField("barcode");

  const handleChange = (e) => {
    // Uppercase on input for immediate visual feedback; final trim is applied
    // at submit time via normalizeBarcode().
    helpers.setValue(e.target.value.toUpperCase());
  };

  return (
    <Field
      label="Barcode"
      required
      htmlFor="barcode"
      hint={BARCODE_HELPER_TEXT}
      error={meta.touched ? meta.error : null}
    >
      <input
        id="barcode"
        name="barcode"
        type="text"
        value={meta.value ?? ""}
        onChange={handleChange}
        onBlur={() => helpers.setTouched(true)}
        placeholder="ABH/18212-01"
        className="pf-input"
        maxLength={BARCODE_MAX_LENGTH}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={meta.touched && !!meta.error}
      />
    </Field>
  );
};

const SpecsSection = () => {
  return (
    <SectionCard
      icon={<Ruler size={16} />}
      title="Specs"
      subtitle="Stock, identifiers, and physical dimensions."
      id="specs"
    >
      <div className="pf-specs">
        <NumericCell
          name="quantity"
          label="Quantity"
          required
          parseValue={(d) => (d === "" ? "" : parseInt(d, 10))}
        />
        <BarcodeCell />
        <NumericCell
          name="height"
          label="Height"
          suffix="cm"
          parseValue={(d) => (d === "" ? "" : parseInt(d, 10))}
        />
        <NumericCell
          name="width"
          label="Width"
          suffix="cm"
          parseValue={(d) => (d === "" ? "" : parseInt(d, 10))}
        />
        {/*
          Position is a *pin*, not a slot number: any value pins the product
          ahead of the entire unpinned catalogue, and 1 is the strongest pin.
          There is no "send to the back" number — unfeaturing means clearing
          the field.

          Hence the two deviations from the other numeric cells:
            * empty parses to `null`, not `""`. The API treats an explicit
              null as "unpin" and `""` as "don't touch" (a bulk sheet with no
              position column must not wipe everyone's pin), so only null
              actually clears it.
            * the placeholder is not "0". A greyed-out 0 reads as the value
              for "none", and 0 sorts *above* 1 — that is how a zero-sales
              product ended up pinned to the top of the storefront.
        */}
        <NumericCell
          name="position"
          label="Display Position"
          placeholder="Not featured"
          suffix="1 = first"
          parseValue={(d) => (d === "" ? null : parseInt(d, 10))}
        />
      </div>
    </SectionCard>
  );
};

export default React.memo(SpecsSection);
